import React from "react";
import { Camera, MapPin, Send, Bell } from "lucide-react";

interface HowItWorksSectionProps {
  onCreateClick: () => void;
}

const steps = [
  {
    number: "01",
    icon: MapPin,
    title: "Repérez le problème",
    description: "Identifiez un problème dans votre espace urbain : nid-de-poule, éclairage défectueux, déchets sauvages, voirie endommagée…"
  },
  {
    number: "02",
    icon: Camera,
    title: "Capturez en 30 secondes",
    description: "Prenez une photo, géolocalisez automatiquement le lieu et décrivez brièvement le signalement."
  },
  {
    number: "03",
    icon: Send,
    title: "Soumettez votre signalement",
    description: "Envoyez directement à votre mairie ou à la communauté. Chaque signalement est traçable et vérifiable."
  },
  {
    number: "04",
    icon: Bell,
    title: "Suivez la résolution",
    description: "Recevez des notifications à chaque changement de statut. 76 % des signalements sont résolus dans les 30 jours."
  }
];

const HowItWorksSection: React.FC<HowItWorksSectionProps> = ({ onCreateClick }) => {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="max-w-2xl mb-16 md:mb-24">
        <span className="ed-eyebrow mb-5 block">Le Processus</span>
        <h2 className="ed-heading text-3xl md:text-5xl mb-5">
          Comment ça <span className="accent">marche ?</span>
        </h2>
        <p className="ed-subtle text-base md:text-lg leading-relaxed max-w-lg">
          Quatre étapes simples pour transformer un constat citoyen en action collective. Rapide, transparent, efficace.
        </p>
      </div>

      {/* Timeline — editorial single column */}
      <div className="relative">
        {/* Central spine */}
        <div className="absolute left-[27px] md:left-[31px] top-2 bottom-2 w-px bg-gradient-to-b from-[var(--ed-navy-500)] via-[var(--ed-rule)] to-transparent opacity-40" />

        <div className="space-y-16 md:space-y-20">
          {steps.map((step, idx) => (
            <div
              key={step.number}
              className="relative flex gap-6 md:gap-10 group ed-fade-up"
              style={{ animationDelay: `${idx * 120}ms` }}
            >
              {/* Step marker */}
              <div className="relative z-10 shrink-0">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-[var(--ed-paper)] border border-[var(--ed-rule-strong)] flex items-center justify-center shadow-sm group-hover:border-[var(--ed-navy-500)] group-hover:shadow-md transition-all duration-500">
                  <span className="ed-numeric text-xl md:text-2xl text-[var(--ed-navy-800)]">
                    {step.number}
                  </span>
                </div>
              </div>

              {/* Content card */}
              <div className="pt-1 md:pt-2 flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-[var(--ed-navy-800)]/5 flex items-center justify-center">
                    <step.icon
                      size={16}
                      strokeWidth={1.5}
                      className="text-[var(--ed-navy-500)]"
                    />
                  </div>
                  <h3 className="font-display font-semibold text-lg md:text-xl text-[var(--ed-ink)] tracking-tight">
                    {step.title}
                  </h3>
                </div>
                <p className="text-[rgba(10,15,31,0.60)] text-sm md:text-base leading-[1.7] font-light max-w-md">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Editorial CTA */}
      <div className="mt-16 md:mt-24 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
        <div className="h-px w-16 bg-[var(--ed-navy-500)] hidden sm:block" />
        <button
          onClick={onCreateClick}
          className="ed-btn-primary text-sm md:text-base"
        >
          Faire un signalement
        </button>
        <span className="text-xs text-[rgba(10,15,31,0.40)] font-body tracking-wide hidden sm:inline">
          Gratuit · 30 secondes · Sans inscription
        </span>
      </div>
    </div>
  );
};

export default HowItWorksSection;
